import React, { useEffect, useState, useRef } from 'react';
import { useDraggable } from './hooks/useDraggable';
import { adjustPositionToViewport, getEditorBoundary } from './utils/viewportUtils';
import './TextSelectionPopup.css';

export default function TextSelectionPopup({
  position,
  onGenerateVersions,
  isGenerating,
  customContext,
  onCustomContextChange,
  onPositionChange,
}) {
  const popupRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [showContextInput, setShowContextInput] = useState(false);
  const [editorBoundary, setEditorBoundary] = useState(null);
  const previousPositionRef = useRef(position);

  // Use the custom draggable hook
  const { isDragging, handleDragStart, userHasDragged, setUserHasDragged } = useDraggable(
    adjustedPosition,
    (newPosition) => {
      setAdjustedPosition(newPosition);
      if (onPositionChange) {
        onPositionChange(newPosition);
      }
    },
    popupRef,
    {
      bounds: { left: 10, top: 10, right: 10, bottom: 10 },
      editorBoundary
    }
  );

  // Get editor boundary on mount and when position changes
  useEffect(() => {
    const boundary = getEditorBoundary();
    setEditorBoundary(boundary);
  }, [position]);

  useEffect(() => {
    // Only update position if:
    // 1. User hasn't manually dragged it, OR
    // 2. The position has significantly changed (new selection)
    if (position && !isDragging) {
      const isNewSelection = !previousPositionRef.current ||
        Math.abs(position.x - previousPositionRef.current.x) > 50 ||
        Math.abs(position.y - previousPositionRef.current.y) > 50;

      if (!userHasDragged || isNewSelection) {
        setAdjustedPosition(position);
        setUserHasDragged(false); // Reset for new selection
      }

      previousPositionRef.current = position;
    }
  }, [position, isDragging, userHasDragged]);

  useEffect(() => {
    if (popupRef.current && adjustedPosition && !isDragging && editorBoundary) {
      const popup = popupRef.current;
      const rect = popup.getBoundingClientRect();

      const newPosition = adjustPositionToViewport(adjustedPosition, rect, {
        margin: 10,
        allowFlip: false, // Don't flip since we want it on the left
        preferLeft: true,  // New option to handle left positioning
        editorBoundary
      });

      // Only update if position changed
      if (newPosition.x !== adjustedPosition.x || newPosition.y !== adjustedPosition.y) {
        setAdjustedPosition(newPosition);
        // Notify parent of position change
        if (onPositionChange) {
          onPositionChange(newPosition);
        }
      }
    }
  }, [adjustedPosition, isDragging, onPositionChange, editorBoundary]);


  if (!position || !adjustedPosition) return null;

  // Calculate max height based on editor boundary
  const maxHeight = editorBoundary ?
    Math.max(200, editorBoundary.bottom - adjustedPosition.y - 20) : undefined;

  return (
    <div
      ref={popupRef}
      className={`text-selection-popup ${isDragging ? 'dragging' : ''}`}
      style={{
        left: `${adjustedPosition.x}px`,
        top: `${adjustedPosition.y}px`,
        maxHeight: maxHeight ? `${maxHeight}px` : undefined,
        overflowY: maxHeight && maxHeight < 400 ? 'auto' : undefined,
      }}
    >
      <div 
        className="popup-header" 
        onMouseDown={handleDragStart}
      >
        <span className="drag-handle">⋮⋮</span>
        <span className="popup-title">Draft Versions</span>
      </div>
      <div className="popup-buttons">
        <button
          onClick={() => onGenerateVersions('lenient')}
          disabled={isGenerating}
          className="popup-button lenient"
        >
          <span className="button-icon">🕊️</span>
          <span className="button-label">Lenient</span>
          <span className="button-description">Favors other party</span>
        </button>
        <button
          onClick={() => onGenerateVersions('neutral')}
          disabled={isGenerating}
          className="popup-button neutral"
        >
          <span className="button-icon">⚖️</span>
          <span className="button-label">Neutral</span>
          <span className="button-description">Balanced</span>
        </button>
        <button
          onClick={() => onGenerateVersions('strict')}
          disabled={isGenerating}
          className="popup-button strict"
        >
          <span className="button-icon">🛑</span>
          <span className="button-label">Strict</span>
          <span className="button-description">Favors your party</span>
        </button>
      </div>
      
      <div className="context-section">
        <button
          onClick={() => setShowContextInput(!showContextInput)}
          disabled={isGenerating}
          className="context-toggle-button"
        >
          <span className="button-icon">{showContextInput ? '−' : '+'}</span>
          <span className="button-label">Add Context</span>
        </button>
        
        {showContextInput && (
          <div className="context-input-container">
            <textarea
              value={customContext}
              onChange={(e) => onCustomContextChange(e.target.value)}
              placeholder="Add any specific instructions or context for the rewrite..."
              className="context-textarea"
              rows={3}
            />
            {customContext && (
              <button
                onClick={() => onCustomContextChange('')}
                className="clear-context-button"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>
      
      {isGenerating && (
        <div className="popup-loading">
          <div className="loading-spinner"></div>
          <span>Generating versions...</span>
        </div>
      )}
    </div>
  );
}

